import React, { useCallback, useEffect, useRef, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { motion } from "framer-motion";
import "./index.scss";

// import GaugeChart from 'react-gauge-chart';

import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";
import Button from "../../../../../components/ui/Button";
import RadioButton from "../../../../../components/ui/RadioButton";

import { CalenderIcon, yearDropdownIcon } from "../../../../../utils/icons";

import { useSelector, useDispatch } from 'react-redux';
import { getDomainHackerScore } from "../../../../../store/actions/dashboardAction";

const DomainHackerScore = ({ domainId, scanId }) => {

	const domainHackerScoreResonse = useSelector((state) => state.dashboard.domainHackerScore)

	const prevDomainIdRef = useRef(null);
	const prevScanIdRef = useRef(null);
	const filterWrapperRef = useRef(null);

	const dispatch = useDispatch();

	const [activeTab, setActiveTab] = useState("2026");
	const [domainHackerLoading, setDomainHackerLoading] = useState(false);
	const [domainHackerScoreList, setDomainHackerScoreList] = useState(null);
	const [showCustomYear, setShowCustomYear] = useState(false);
	const [selectedYear, setSelectedYear] = useState("");
	const [updatedYear, setUpdatedYear] = useState("")

	const fetchDomainHackerScore = useCallback(() => {
		const params = domainId.toLowerCase() !== "all" ? `&domain=${domainId}` : "";
		const year = activeTab !== "" ? `&year=${activeTab}` : `&year=${updatedYear}`;
		const url = `${params}${year}`
		if (domainId.toLowerCase() !== "all") {
			setDomainHackerLoading(true)
			dispatch(getDomainHackerScore(url))
		}
	}, [domainId, activeTab, updatedYear, dispatch])

	useEffect(() => {
		fetchDomainHackerScore()
	}, [activeTab, updatedYear, fetchDomainHackerScore])

	useEffect(() => {
		if (domainId !== prevDomainIdRef.current || scanId !== prevScanIdRef.current) {
			fetchDomainHackerScore();
			prevDomainIdRef.current = domainId;
			prevScanIdRef.current = scanId;
		}
	}, [domainId, scanId, fetchDomainHackerScore])

	useEffect(() => {
		if (domainHackerScoreResonse !== null) {
			setDomainHackerLoading(false)
			setDomainHackerScoreList(domainHackerScoreResonse)
		}
	}, [domainHackerScoreResonse]);

	useEffect(() => {
		if (domainHackerScoreList !== null) {
			let data = []
			if (domainHackerScoreList?.status === "failed" || domainHackerScoreList === "Not Found") {
				data = []
			} else {
				data = domainHackerScoreList?.stats?.domain_score?.filter(item =>
					typeof item?.score === "number" && !isNaN(item?.score)
				) || [];
			}

			// Create root element
			let root = am5.Root.new("scorechartdiv");

			// Set themes
			root.setThemes([am5themes_Animated.new(root)]);

			// Create chart
			let chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					panX: true,
					panY: true,
					wheelX: "none", // Disable mouse wheel zoom
					wheelY: "none",
					pinchZoomX: false, // Disable pinch zoom on X-axis
					pinchZoomY: false, // Disable pinch zoom on Y-axis
				})
			);

			chart.get("colors").set("step", 2);

			// Disable "Created with amCharts 5" label
			root._logo.dispose();

			// Create axes
			let xRenderer = am5xy.AxisRendererX.new(root, {
				strokeOpacity: 0.2,
			});

			// Remove X-axis line
			xRenderer.set("visible", false);

			// Remove grid lines and ticks
			xRenderer.grid.template.set("visible", false);
			xRenderer.ticks.template.set("visible", false);

			let xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					categoryField: "name",
					renderer: xRenderer,
					tooltip: null,
				})
			);

			let yRenderer = am5xy.AxisRendererY.new(root, {
				strokeOpacity: 0.2,
			});

			let yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					min: 0,
					max: 950,
					strictMinMax: true,
					renderer: yRenderer,
					calculateTotals: false,
					numberFormat: "#a",
					tooltip: null,
				})
			);

			// Force step of 100
			yAxis.setAll({
				interval: 100,
				extraMin: 0,
				extraMax: 0
			});

			yRenderer.setAll({
				minGridDistance: 20,
				visible: true,
				strokeOpacity: 0.1,
				inside: false
			});

			yRenderer.labels.template.setAll({
				multiLine: false,
				maxWidth: 100,
				paddingTop: -5,
				oversizedBehavior: "wrap",
				fontSize: 14,
				paddingRight: 10
			});


			yRenderer.set("visible", false);
			// Remove grid lines and ticks
			yRenderer.grid.template.set("visible", false);
			yRenderer.ticks.template.set("visible", false);

			// Add background ranges for Y-axis
			let range1 = yAxis.createAxisRange(
				yAxis.makeDataItem({
					value: 0,
					endValue: 250,
				})
			);

			range1.get("axisFill").setAll({
				fill: am5.color("#EA635D"),
				fillOpacity: 0.8,
				visible: true,
			});

			let range2 = yAxis.createAxisRange(
				yAxis.makeDataItem({
					value: 250,
					endValue: 500,
				})
			);

			range2.get("axisFill").setAll({
				fill: am5.color("#F0965A"),
				fillOpacity: 0.8,
				visible: true,
			});

			let range3 = yAxis.createAxisRange(
				yAxis.makeDataItem({
					value: 500,
					endValue: 800,
				})
			);

			range3.get("axisFill").setAll({
				fill: am5.color("#F4DA6B"),
				fillOpacity: 0.8,
				visible: true,
			});

			let range4 = yAxis.createAxisRange(
				yAxis.makeDataItem({
					value: 800,
					endValue: 950,
				})
			);
			range4.get("axisFill").setAll({
				fill: am5.color("#94D86B"),
				fillOpacity: 0.8,
				visible: true,
			});

			// Create series
			let series = chart.series.push(
				am5xy.LineSeries.new(root, {
					calculateAggregates: true,
					xAxis: xAxis,
					yAxis: yAxis,
					valueYField: "score",
					categoryXField: "name",
					tooltip: am5.Tooltip.new(root, {
						pointerOrientation: "horizontal",
						html: `<div class="custom-tooltip-container">
                    <div class="tooltip-heading"><h2 class="m-0">Score: {valueY}</h2></div>
                    <div class="align-items-center w-100">
                      <span class="tooltip-key d-inline-block">{categoryX}</span>
                    </div>
                  </div>`, // Use your custom HTML
						getFillFromSprite: false,
						background: am5.PointedRectangle.new(root, {
							fill: am5.color(0xffffff), // Fixed white background
							fillOpacity: 1,           // Fully opaque
							stroke: am5.color(0xFFFFFF), // Border color
							strokeWidth: 1,           // Border width
							pointerBaseWidth: 10,      // Reduce pointer base width
							pointerLength: 8,         // Reduce pointer length to minimize space
							cornerRadius: 10,          // Optional rounded corners
							shadowColor: am5.color(0xeaf1ff), // Shadow color
							shadowBlur: 40.9,                 // Amount of blur for the shadow
							shadowOffsetX: 5,               // Horizontal shadow offset
							shadowOffsetY: 5,
							height: 108
						}),
					}),
				})
			);

			series.set("stroke", am5.color("#062465"))

			// Add circle bullet
			series.bullets.push(function () {
				let graphics = am5.Circle.new(root, {
					strokeWidth: 2,
					radius: 3,
					stroke: series.get("stroke"),
					fill: am5.color("#062465"),
				});
				return am5.Bullet.new(root, {
					sprite: graphics,
				});
			});

			// Add cursor
			chart.set(
				"cursor",
				am5xy.XYCursor.new(root, {
					xAxis: xAxis,
					yAxis: yAxis,
					snapToSeries: [series],
					behavior: "none", // Prevent zooming
				})
			);

			if (data.length > 0) {
				series.data.setAll(data);
				xAxis.data.setAll(data);
			}

			// Make stuff animate on load
			series.appear(1000);
			chart.appear(1000, 100);

			// Cleanup on unmount
			return () => {
				root.dispose();
			};
		}
	}, [domainHackerScoreList])


	// const chartStyle = {
	//   height: 60,
	//   width: 120,
	// }

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);

		function handleClickOutside(event) {
			if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
				if (showCustomYear) {
					setShowCustomYear(false)
				}
			}

		}
	}, [showCustomYear])

	return (
		<>
			<div className="bg-white-with-shadow p-4 mt-4">
				{
					domainHackerLoading ?
						<SkeletonLoader type={"line"} count={7} currentPage={"domainHackerScore"} />
						:
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.4 }}
						>
							<div className="dashboard-box-container">
								<p className="heading font-bold">Overall Trend Score</p>
							</div>
							<div className="d-flex align-items-center mt-3">
								<div className="discovered-date-selection d-flex me-5 gap-3">
									<p className={`rounded text-center ${activeTab === "2026" ? "active" : ""}`}
										onClick={() => {
											setUpdatedYear("");
											setSelectedYear("");
											setActiveTab("2026");
										}}>
										2026
									</p>
									<p className={`rounded text-center ${activeTab === "2025" ? "active" : ""}`}
										onClick={() => {
											setUpdatedYear("");
											setSelectedYear("");
											setActiveTab("2025");
										}}>
										Previous Year
									</p>
								</div>
								<div className={`rounded text-center position-relative custom-year-container`}>
									<p className="custom-text" onClick={() => { setShowCustomYear(true) }}>{CalenderIcon()} {updatedYear === "" ? 'Select Year' : updatedYear} <span className={`arrow ${showCustomYear ? 'active' : ""}`}>{yearDropdownIcon()}</span></p>
									{showCustomYear && <div className="year-dropdown-container position-absolute p-3 rounded" ref={filterWrapperRef}>
										<h6 className="year-heading">Select Year</h6>
										<div className="year-option-container">
											<div className="year-option">
												<RadioButton radioId={"2025"} radioLabel={"2025"} radioValue={"2025"} onChange={() => { setSelectedYear("2025") }} isChecked={selectedYear === "2025" ? true : false} />
											</div>
											<div className="year-option">
												<RadioButton radioId={"2024"} radioLabel={"2024"} radioValue={"2024"} onChange={() => { setSelectedYear("2024") }} isChecked={selectedYear === "2024" ? true : false} />
											</div>
											<div className="year-option">
												<RadioButton radioId={"2023"} radioLabel={"2023"} radioValue={"2023"} onChange={() => { setSelectedYear("2023") }} isChecked={selectedYear === "2023" ? true : false} />
											</div>
											<div className="year-option">
												<RadioButton radioId={"2022"} radioLabel={"2022"} radioValue={"2022"} onChange={() => { setSelectedYear("2022") }} isChecked={selectedYear === "2022" ? true : false} />
											</div>
											<div className="year-option">
												<RadioButton radioId={"2021"} radioLabel={"2021"} radioValue={"2021"} onChange={() => { setSelectedYear("2021") }} isChecked={selectedYear === "2021" ? true : false} />
											</div>
										</div>
										<div className="filter-apply-btn mt-3">
											<Button
												buttonText={"Done"}
												buttonType={"primary"}
												buttonClicked={() => {
													setActiveTab("")
													setUpdatedYear(selectedYear)
													setShowCustomYear(false)
												}} />
										</div>
									</div>}
								</div>
							</div>
							<div className="d-flex justify-content-between">
								{/* <div className="domain-hack-score d-flex align-items-center p-4 mt-3">
									<GaugeChart 
										id="gauge-chart1"
										nrOfLevels={4} 
										colors={["#ee6862", "#f2a16b", "#F4DA6B", "#8dd761"]}
										percent={domainHackerScoreList?.stats?.domain_score[domainHackerScoreList?.stats?.domain_score?.length - 1]?.score/900 || 0}
										arcWidth={0.2}
										arcPadding={0}
										cornerRadius={0}
										hideText={true}
										style={chartStyle}
									/>
									<div className="text-center ms-3 custom-spacing">
										<p className="count-content mb-0 mt-2">{domainHackerScoreList?.stats?.domain_score[domainHackerScoreList?.stats?.domain_score?.length - 1]?.score}</p>
										<p className="hack mt-0 ms-3">Latest score</p>
									</div>
									</div> */}

														{/* <div className="domain-prev d-flex align-items-center p-4">
									<div className="text-center d-flex  custom-spacing hack-score-count">
										<p className="hack mt-0">
										<span className="prev-hack"></span> Previous Scan
										</p>
										<p className="hack mt-0 ms-3">
										<span className="late-hack"></span> Latest Scan
										</p>
									</div>
									</div> */}
							</div>
							<div
								id="scorechartdiv"
								style={{
									// position: "absolute",
									width: "90%",
									height: "320px",
									// zIndex: 10, // Ensure it's above other elements
									paddingLeft: "60px",
									paddingTop: "50px"
								}}
							>
							</div>

							<div className="domain-hack-main-content p-4"></div>
						</motion.div>
				}
			</div>
		</>

	);
};
export default DomainHackerScore;
